import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, Text as RNText, View } from 'react-native';

import { Button, Text } from '@/components/ui';
import { fetchCurrencies, fetchFxFromApi, fetchFxRates, putFxRates } from '@/lib/finance/api';
import {
  addMonths,
  currentMonth,
  monthLabelShort,
  monthStart,
  recentMonths,
} from '@/lib/finance/month';
import type { Currency, FxRate } from '@/lib/finance/types';

const COLUMNS = 6;

type Cell = { rate: number | null; source: FxRate['source'] | null };

function rateKey(month: string, code: string): string {
  return `${month}|${code}`;
}

export function FxRatesEditor() {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [cells, setCells] = useState<Map<string, Cell>>(new Map());
  const [windowEnd, setWindowEnd] = useState<string>(currentMonth());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);

  const months = recentMonths(windowEnd, COLUMNS);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [currencyRows, rateRows] = await Promise.all([
        fetchCurrencies(),
        fetchFxRates(monthStart(months[0]), monthStart(windowEnd)),
      ]);
      setCurrencies(currencyRows);
      const map = new Map<string, Cell>();
      for (const row of rateRows) {
        const month = row.month.slice(0, 7);
        map.set(rateKey(month, row.currencyCode), { rate: row.rateToUsd, source: row.source });
      }
      setCells(map);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load FX rates');
    } finally {
      setLoading(false);
    }
  }, [windowEnd]);

  useEffect(() => {
    void load();
  }, [load]);

  async function commit(month: string, code: string, raw: string) {
    const trimmed = raw.trim();
    const key = rateKey(month, code);
    const existing = cells.get(key);
    if (trimmed === '') {
      return;
    }
    const value = Number(trimmed);
    if (!Number.isFinite(value) || value <= 0) {
      setError(`Rate for ${code} ${month} must be a positive number`);
      return;
    }
    if (existing && existing.rate === value && existing.source === 'manual') {
      return;
    }
    setSavingKey(key);
    setError(null);
    try {
      await putFxRates(monthStart(month), [{ currencyCode: code, rateToUsd: value }]);
      setCells((prev) => {
        const next = new Map(prev);
        next.set(key, { rate: value, source: 'manual' });
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rate');
    } finally {
      setSavingKey(null);
    }
  }

  async function onFetchFromApi() {
    setFetching(true);
    setError(null);
    try {
      const results = await fetchFxFromApi({ from: months[0], to: windowEnd });
      await load();
      const skipped = results.reduce((sum, r) => sum + r.skippedManual.length, 0);
      const missing = new Set(results.flatMap((r) => r.missing));
      if (missing.size > 0) {
        setError(
          `No rate from the provider for: ${[...missing].join(', ')}${
            skipped > 0 ? ` · ${skipped} manual cell(s) left untouched` : ''
          }`,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch rates');
    } finally {
      setFetching(false);
    }
  }

  const editableCurrencies = currencies.filter((c) => c.code !== 'USD');

  return (
    <View>
      <View className="mb-1 flex-row flex-wrap items-center gap-3">
        <Text variant="h3">FX rates</Text>
        <View className="flex-1" />
        <View className="flex-row items-center gap-1">
          <Pressable
            aria-label="Older months"
            className="rounded px-2 py-1 hover:bg-surface-raised"
            onPress={() => setWindowEnd(addMonths(windowEnd, -COLUMNS))}
          >
            <RNText className="text-base text-fg-muted">‹</RNText>
          </Pressable>
          <Text variant="body-sm" className="min-w-[150px] text-center text-muted">
            {monthLabelShort(months[0]!)} – {monthLabelShort(windowEnd)}
          </Text>
          <Pressable
            aria-label="Newer months"
            className="rounded px-2 py-1 hover:bg-surface-raised"
            onPress={() => setWindowEnd(addMonths(windowEnd, COLUMNS))}
          >
            <RNText className="text-base text-fg-muted">›</RNText>
          </Pressable>
        </View>
        <Button
          label={fetching ? 'Fetching…' : 'Fetch from API'}
          size="sm"
          variant="secondary"
          className="shrink-0 whitespace-nowrap"
          disabled={fetching || editableCurrencies.length === 0}
          onPress={() => void onFetchFromApi()}
        />
      </View>
      <Text variant="caption" className="mb-3 text-muted">
        Units of each currency per 1 USD. Editing a cell marks it manual so the automatic monthly
        fetch never overwrites it. USD is pinned at 1.
      </Text>

      {error ? (
        <Text variant="body-sm" className="mb-3 text-danger">
          {error}
        </Text>
      ) : null}

      {loading ? (
        <ActivityIndicator />
      ) : editableCurrencies.length === 0 ? (
        <Text variant="body" className="text-muted">
          Add currencies first (Currencies tab).
        </Text>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="border-b border-border px-3 py-2 text-left font-medium text-fg-muted">
                  Currency
                </th>
                {months.map((month) => (
                  <th
                    key={month}
                    className="border-b border-border px-3 py-2 text-right font-medium text-fg-muted"
                  >
                    {monthLabelShort(month)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border-b border-border px-3 py-2 font-medium">USD</td>
                {months.map((month) => (
                  <td
                    key={month}
                    className="border-b border-border px-3 py-2 text-right text-fg-subtle"
                  >
                    1
                  </td>
                ))}
              </tr>
              {editableCurrencies.map((currency) => (
                <tr key={currency.code}>
                  <td className="border-b border-border px-3 py-2 font-medium">{currency.code}</td>
                  {months.map((month) => {
                    const key = rateKey(month, currency.code);
                    const cell = cells.get(key);
                    return (
                      <td key={month} className="border-b border-border px-2 py-1 text-right">
                        <input
                          key={`${key}:${cell?.rate ?? ''}:${cell?.source ?? ''}`}
                          className="w-24 rounded border border-transparent px-2 py-1 text-right hover:border-border focus:border-accent"
                          defaultValue={cell?.rate != null ? String(cell.rate) : ''}
                          disabled={savingKey === key}
                          inputMode="decimal"
                          placeholder="—"
                          onBlur={(e) => void commit(month, currency.code, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              (e.target as HTMLInputElement).blur();
                            }
                          }}
                        />
                        <div
                          className={`text-[10px] ${
                            cell?.source === 'manual'
                              ? 'text-accent'
                              : cell?.source === 'api'
                                ? 'text-fg-subtle'
                                : 'text-transparent'
                          }`}
                        >
                          {cell?.source ?? '·'}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </View>
  );
}
